/**
 * Product Hub MVP Configuration
 *
 * This configuration controls which features are visible in the MVP release.
 * Features are hidden (not deleted) to allow easy re-enabling in future phases.
 *
 * RBAC SIMPLIFICATION:
 * For MVP, bypassRoleChecks is set to true, granting all users System Administrator
 * privileges. This simplifies the initial launch while preserving the full 6-role
 * system for post-MVP restoration.
 *
 * See: /docs/mvp/RBAC_SYSTEM_DOCUMENTATION.md for full role documentation
 *
 * @version 1.0.0
 * @date December 2025
 */

// =============================================================================
// TYPES
// =============================================================================

export interface MVPConfig {
  /** MVP mode enabled - when true, applies all feature restrictions */
  mvpMode: boolean;

  /** Applications visible in App Launcher */
  enabledApplications: ApplicationId[];

  /** Objects visible in Object Manager and throughout the system */
  enabledObjects: ObjectName[];

  /** System Admin features that are enabled */
  enabledAdminFeatures: AdminFeature[];

  /** Individual feature flags for granular control */
  featureFlags: FeatureFlags;

  /** Navigation items to hide */
  hiddenNavItems: string[];

  /** Routes to redirect (show "Coming Soon" or redirect to home) */
  disabledRoutes: string[];

  /** Navigation items that are enabled */
  enabledNavItems: NavigationItem[];

  /** Navigation groups that are enabled */
  enabledNavGroups: NavigationGroup[];
}

export type ApplicationId =
  | 'regal-main'
  | 'cybersweep'
  | 'system-admin'
  | 'credit-management'
  | 'credit-block';

export type ObjectName =
  | 'Client'
  | 'Client__c'
  | 'CyberSweepClient'
  | 'CyberSweepClient__c'
  | 'CSAudit'
  | 'CSAudit__c'
  | 'PAWRecord'
  | 'PAWRecord__c'
  | 'User'
  | 'User__c'
  | 'Note'
  | 'Note__c'
  | 'Task'
  | 'Task__c'
  | 'Case'
  | 'Case__c'
  | 'Event'
  | 'Event__c'
  | 'Lead'
  | 'Lead__c'
  | 'Contact'
  | 'Contact__c'
  | 'Opportunity'
  | 'Opportunity__c'
  | 'Account'
  | 'Account__c'
  | 'Contract'
  | 'Contract__c'
  | 'Report'
  | 'Report__c'
  | 'CreditBlock'
  | 'CreditBlock__c'
  | 'Credit_Bureaus'
  | 'Credit_Bureaus__c'
  | 'Unlock_Request'
  | 'Unlock_Request__c'
  | 'CreditReport'
  | 'CreditReport__c'
  | 'EmailTemplate'
  | 'EmailTemplate__c';

/** Navigation item identifiers */
export type NavigationItem =
  | 'home'
  | 'clients'
  | 'leads'
  | 'contacts'
  | 'opportunities'
  | 'cases'
  | 'tasks'
  | 'events'
  | 'dashboard'
  | 'reports'
  | 'system-admin';

/** Navigation group identifiers */
export type NavigationGroup =
  | 'sales'
  | 'billing'
  | 'service'
  | 'productivity'
  | 'analytics';

export type AdminFeature =
  | 'setup-home'
  | 'object-manager'
  | 'page-layouts'
  | 'record-pages'
  | 'list-views'
  | 'workflow-rules'
  | 'approval-processes'
  | 'user-management'
  | 'email-templates'
  | 'notes'
  | 'tasks'
  | 'cases'
  | 'data-retention'
  | 'validation-rules'
  | 'webhooks'
  | 'reports'
  | 'scheduled-jobs';

export interface FeatureFlags {
  // Applications
  showCreditManagement: boolean;
  showCreditBlock: boolean;

  // Objects
  showLeads: boolean;
  showContacts: boolean;
  showOpportunities: boolean;

  // Features
  showReports: boolean;
  showDataRetention: boolean;
  showValidationRules: boolean;
  showWebhooks: boolean;
  showScheduledJobs: boolean;
  showProcessBuilder: boolean;
  showFlowBuilder: boolean;

  // Advanced Admin
  showDatabaseAdmin: boolean;
  showSystemMonitoring: boolean;
  showAPIManagement: boolean;
  showSystemLogs: boolean;

  // Detail Page Tabs - DISABLED for MVP (PE-770)
  // These tabs are hidden because they lack backend implementation
  showFilesTab: boolean;
  showProductsTab: boolean;
  showEmailsTab: boolean;

  // RBAC - ENABLED to simplify permissions for MVP
  // When true, all users get System Administrator privileges
  bypassRoleChecks: boolean;
}

// =============================================================================
// MVP CONFIGURATION
// =============================================================================

export const MVP_CONFIG: MVPConfig = {
  mvpMode: true,

  // -------------------------------------------------------------------------
  // ENABLED APPLICATIONS (shown in App Launcher)
  // Visibility per-user is driven by Gobodo RBAC (useAccessibleApps).
  // -------------------------------------------------------------------------
  enabledApplications: [
    'regal-main',
    'cybersweep',
    'system-admin',
    'credit-management',
    'credit-block',
  ],

  // -------------------------------------------------------------------------
  // ENABLED OBJECTS (shown in Object Manager)
  // -------------------------------------------------------------------------
  enabledObjects: [
    'Client',
    'CyberSweepClient',
    'CSAudit',
    'PAWRecord',
    'User',
    'Note',
    'Task',
    'Case',
    'Event',
    // Added for Gobodo integration
    'Lead',
    'Lead__c',
    'Contact',
    'Contact__c',
    'Opportunity',
    'Opportunity__c',
    'Account',
    'Account__c',
    'Contract',
    'Contract__c',
    'Report',
    'Report__c',
    // Credit Block objects
    'CreditBlock',
    'CreditBlock__c',
    'Credit_Bureaus',
    'Credit_Bureaus__c',
    'Unlock_Request',
    'Unlock_Request__c',
    // Credit Report and Email Template
    'CreditReport',
    'CreditReport__c',
    'EmailTemplate',
    'EmailTemplate__c',
  ],

  // -------------------------------------------------------------------------
  // ENABLED ADMIN FEATURES
  // -------------------------------------------------------------------------
  enabledAdminFeatures: [
    'setup-home',
    'object-manager',
    'page-layouts',
    'record-pages',
    'list-views',
    'workflow-rules',
    'approval-processes',
    'user-management',
    'email-templates',
    'notes',
    'tasks',
    'cases',
  ],

  // -------------------------------------------------------------------------
  // FEATURE FLAGS
  // -------------------------------------------------------------------------
  featureFlags: {
    // Applications - DISABLED for MVP
    showCreditManagement: false,
    showCreditBlock: true,

    // Objects - ENABLED for Gobodo integration
    showLeads: true,
    showContacts: true,
    showOpportunities: true,

    // Features - ENABLED for Gobodo integration
    showReports: true,
    showDataRetention: false,
    showValidationRules: false,
    showWebhooks: false,
    showScheduledJobs: false,
    showProcessBuilder: false,
    showFlowBuilder: false,

    // Advanced Admin - DISABLED for MVP
    showDatabaseAdmin: false,
    showSystemMonitoring: false,
    showAPIManagement: false,
    showSystemLogs: false,

    // Detail Page Tabs - DISABLED for MVP (PE-770)
    // These tabs are hidden because they lack backend implementation
    showFilesTab: false,
    showProductsTab: false,
    showEmailsTab: false,

    // =====================================================================
    // RBAC ENFORCEMENT (PE-737 / PE-1020)
    // =====================================================================
    // When true: All users have System Administrator privileges
    // When false: 7-role permission system is enforced
    //
    // Set to FALSE for production — full RBAC via Gobodo roles + CASL.
    // See: /docs/RBAC_OPERATIONALIZATION.md
    // =====================================================================
    bypassRoleChecks: false,
  },

  // -------------------------------------------------------------------------
  // HIDDEN NAVIGATION ITEMS
  // -------------------------------------------------------------------------
  hiddenNavItems: [
    '/credit-management',
    '/admin/data-retention',
    '/admin/validation-rules',
    '/admin/webhooks',
    '/admin/scheduled-jobs',
    '/admin/process-builder',
    '/admin/flow',
    '/admin/database',
    '/admin/monitoring',
    '/admin/api-management',
    '/admin/system-logs',
  ],

  // -------------------------------------------------------------------------
  // DISABLED ROUTES (redirect to home or show "Coming Soon")
  // -------------------------------------------------------------------------
  disabledRoutes: [
    '/credit-management',
    '/credit-management/*',
    '/admin/data-retention',
    '/admin/data-retention/*',
    '/admin/validation-rules',
    '/admin/validation-rules/*',
  ],

  // -------------------------------------------------------------------------
  // ENABLED NAVIGATION ITEMS
  // -------------------------------------------------------------------------
  enabledNavItems: [
    'home',
    'clients',
    'leads',
    'contacts',
    'opportunities',
    'cases',
    'tasks',
    'events',
    'dashboard',
    'reports',
    'system-admin',
  ],

  // -------------------------------------------------------------------------
  // ENABLED NAVIGATION GROUPS
  // -------------------------------------------------------------------------
  enabledNavGroups: [
    'sales',
    'billing',
    'service',
    'productivity',
    'analytics',
  ],
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if MVP mode is enabled
 * Returns false when bypass mode is enabled (Demo Mode) to unlock all features
 */
export function isMVPMode(): boolean {
  // Check if bypass mode is enabled via localStorage
  try {
    const bypassEnabled = localStorage.getItem('regal_mvp_bypass_mode') === 'true';
    if (bypassEnabled) {
      return false; // Demo Mode - all features unlocked
    }
  } catch {
    // localStorage not available
  }
  return MVP_CONFIG.mvpMode;
}

/**
 * Check if a specific feature flag is enabled
 */
export function isFeatureEnabled(feature: keyof FeatureFlags): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return true; // All features enabled when not in MVP mode
  }
  return MVP_CONFIG.featureFlags[feature];
}

/**
 * Check if an application is enabled
 */
export function isApplicationEnabled(appId: ApplicationId): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return true;
  }
  return MVP_CONFIG.enabledApplications.includes(appId);
}

/**
 * Check if an object is enabled
 */
export function isObjectEnabled(objectName: ObjectName): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return true;
  }
  return MVP_CONFIG.enabledObjects.includes(objectName);
}

/**
 * Check if an admin feature is enabled
 */
export function isAdminFeatureEnabled(feature: AdminFeature): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return true;
  }
  return MVP_CONFIG.enabledAdminFeatures.includes(feature);
}

/**
 * Check if a navigation item should be hidden
 */
export function isNavItemHidden(path: string): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return false;
  }
  return MVP_CONFIG.hiddenNavItems.some(
    hiddenPath => path === hiddenPath || path.startsWith(hiddenPath + '/')
  );
}

/**
 * Check if a route is disabled
 */
export function isRouteDisabled(path: string): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return false;
  }
  return MVP_CONFIG.disabledRoutes.some(disabledPath => {
    if (disabledPath.endsWith('/*')) {
      const basePath = disabledPath.slice(0, -2);
      return path === basePath || path.startsWith(basePath + '/');
    }
    return path === disabledPath;
  });
}

/**
 * Check if RBAC checks should be bypassed (for MVP simplification)
 *
 * When this returns true:
 * - All users have full System Administrator permissions
 * - Role checks are skipped in permission helpers
 * - Users can access all features regardless of assigned role
 *
 * When this returns false:
 * - Full 6-role RBAC system is enforced
 * - Users only have permissions defined by their role
 * - Role hierarchy is respected
 */
/** Bypass when config enables it, or when regal_mvp_bypass_mode is set in localStorage (explicit override). */
export function shouldBypassRoleChecks(): boolean {
  if (MVP_CONFIG.mvpMode && MVP_CONFIG.featureFlags.bypassRoleChecks) return true;
  if (typeof window !== 'undefined' && localStorage.getItem('regal_mvp_bypass_mode') === 'true') return true;
  return false;
}

/**
 * Get the default role for MVP mode
 * Returns 'system_admin' when bypassRoleChecks is true
 */
export function getMVPDefaultRole(): string {
  if (shouldBypassRoleChecks()) {
    return 'system_admin';
  }
  return 'employee'; // Default role when RBAC is enforced — actual role comes from Gobodo
}

/**
 * Get list of enabled applications for App Launcher
 */
export function getEnabledApplications(): ApplicationId[] {
  if (!MVP_CONFIG.mvpMode) {
    return ['regal-main', 'cybersweep', 'system-admin', 'credit-management', 'credit-block'];
  }
  return MVP_CONFIG.enabledApplications;
}

/**
 * Get list of enabled objects for Object Manager
 */
export function getEnabledObjects(): ObjectName[] {
  if (!MVP_CONFIG.mvpMode) {
    return [
      'Client', 'CyberSweepClient', 'CSAudit', 'PAWRecord',
      'User', 'Note', 'Task', 'Case', 'Event',
      'Lead', 'Contact', 'Opportunity', 'Report'
    ];
  }
  return MVP_CONFIG.enabledObjects;
}

/**
 * Check if a navigation item is enabled
 */
export function isNavItemEnabled(item: NavigationItem): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return true;
  }
  return MVP_CONFIG.enabledNavItems.includes(item);
}

/**
 * Check if a navigation group is enabled
 */
export function isNavGroupEnabled(group: NavigationGroup): boolean {
  if (!MVP_CONFIG.mvpMode) {
    return true;
  }
  return MVP_CONFIG.enabledNavGroups.includes(group);
}

/**
 * Get list of enabled navigation items
 */
export function getEnabledNavItems(): NavigationItem[] {
  if (!MVP_CONFIG.mvpMode) {
    return ['home', 'clients', 'leads', 'contacts', 'opportunities', 'cases', 'tasks', 'events', 'dashboard', 'reports', 'system-admin'];
  }
  return MVP_CONFIG.enabledNavItems;
}

/**
 * Get list of enabled navigation groups
 */
export function getEnabledNavGroups(): NavigationGroup[] {
  if (!MVP_CONFIG.mvpMode) {
    return ['sales', 'billing', 'service', 'productivity', 'analytics'];
  }
  return MVP_CONFIG.enabledNavGroups;
}

/** Strip Gobodo/env suffixes to get base object name for matching */
function toBaseObjectName(name: string): string {
  return name.replace(/(_stg3_c|__stg3_c|__stg_c|__c)$/i, '').toLowerCase();
}

/**
 * Check if an object is enabled by API name (handles 'Client', 'Client__c', 'Case_stg3_c', etc.)
 */
export function isObjectEnabledByApiName(apiName: string): boolean {
  if (!MVP_CONFIG.mvpMode) return true;
  const apiBase = toBaseObjectName(apiName);
  return MVP_CONFIG.enabledObjects.some(obj => toBaseObjectName(obj) === apiBase);
}

// =============================================================================
// ENVIRONMENT OVERRIDE
// =============================================================================

/**
 * Override MVP mode via environment variable
 * Set REACT_APP_MVP_MODE=false to disable MVP restrictions
 */
const envMVPMode = process.env.REACT_APP_MVP_MODE;
if (envMVPMode === 'false') {
  MVP_CONFIG.mvpMode = false;
  console.info('[MVP Config] MVP mode disabled via environment variable');
}

export default MVP_CONFIG;
